import { DomainError } from '../errors.js';
import { addDays, parseLocalDate } from '../local-date.js';

/**
 * Doküman revizyonunun yaşam döngüsü (ADR 0023 §3, ADR 0027 §9–§10, ADR 0028 #26–#27;
 * `dokuman-kutuphanesi.md` §3.2) [ISM 11.2]. Durum saklanmaz: değiştirilemez
 * `document_revision_events` kayıtlarından ve `asOf` tarihinden türetilir. "Yürürlükten kalktı"
 * (`superseded`) bir olay değildir; halefin yayınından ve yürürlük tarihinden türer. Türetme olay
 * zamanlarını yürürlük tarihiyle karşılaştırmaz; yalnız olay türlerine, sıralarına ve `asOf`'a
 * bakar: aynı olaylar ve aynı `asOf` gemide ve bulutta aynı sonucu verir. Bu modül saftır.
 */

/** Değiştirilemez revizyon olayları. Taslak olay değildir: olayı olmayan revizyon taslaktır. */
export const REVISION_EVENTS = [
  'submitted',
  'returned',
  'approved',
  'published',
  'rescheduled',
  'cancelled',
  'withdrawn',
  'discarded',
] as const;
export type RevisionEventKind = (typeof REVISION_EVENTS)[number];

export const REVISION_STATES = [
  'draft',
  'pending_approval',
  'approved',
  'scheduled',
  'published',
  'superseded',
  'withdrawn',
  'discarded',
  'cancelled',
] as const;
export type RevisionState = (typeof REVISION_STATES)[number];

/** Olay: tür ve (yayın ile ertelemede) yürürlük tarihi, gemi yerel tarihi `YYYY-MM-DD`. */
export interface RevisionEvent {
  event: RevisionEventKind;
  effectiveOn?: string | null;
}

/**
 * Gemiye inen olaylar (ADR 0028 #28; ADR 0032): yalnız yayınlanmış revizyonun onayı ve sonrası.
 * Gönderim, iade ve vazgeçme ofisin iç akışıdır; taslak gemiyi etkilemez (`dokuman-kutuphanesi.md`
 * §3.2). Gemideki geçmiş bu yüzden onay olayından başlar (`revisionState`).
 */
export const REPLICA_EVENTS: readonly RevisionEventKind[] = [
  'approved',
  'published',
  'rescheduled',
  'cancelled',
  'withdrawn',
];

/** Dalda aynı anda yalnız bir tane bulunabilen "açık" durumlar (ADR 0027 §10, ADR 0028 #27). */
export const OPEN_REVISION_STATES: readonly RevisionState[] = [
  'draft',
  'pending_approval',
  'approved',
  'scheduled',
];

/** Gerekçesi zorunlu olaylar: iade, geri çekme, vazgeçme, erteleme ve iptal. */
export const REASON_REQUIRED_EVENTS: readonly RevisionEventKind[] = [
  'returned',
  'withdrawn',
  'discarded',
  'rescheduled',
  'cancelled',
];

/** Saklanan geçişler. `published`, yürürlük tarihine göre `scheduled` ya da `published` verir. */
const TRANSITIONS: Readonly<
  Record<RevisionState, Partial<Readonly<Record<RevisionEventKind, RevisionState>>>>
> = {
  draft: { submitted: 'pending_approval', discarded: 'discarded' },
  pending_approval: { returned: 'draft', approved: 'approved' },
  // Onaylanmış ama yayınlanmamış revizyon da geri çekilebilir; yoksa dal kilitli kalırdı
  approved: { published: 'published', withdrawn: 'withdrawn' },
  scheduled: { rescheduled: 'scheduled', cancelled: 'cancelled', withdrawn: 'withdrawn' },
  published: { withdrawn: 'withdrawn' },
  superseded: {},
  withdrawn: {},
  discarded: {},
  cancelled: {},
};

export interface TransitionContext {
  reason?: string | null;
  /** Yayında yürürlük tarihi; boşsa hemen. */
  effectiveOn?: string | null;
  /** Durumun hesaplandığı gün (`YYYY-MM-DD`). */
  asOf?: string;
}

function released(effective: string | null | undefined, asOf: string | undefined): RevisionState {
  return effective && (asOf === undefined || effective > asOf) ? 'scheduled' : 'published';
}

/**
 * Olayın bu durumdan izinli olup olmadığını denetler ve yeni durumu döner.
 * - Onaylanmamış revizyon yayınlanamaz (`NOT_APPROVED`) [ISM 11.2.2].
 * - Gönderilmemiş revizyon onaylanamaz ya da iade edilemez (`NOT_SUBMITTED`).
 * - İade, geri çekme, vazgeçme, erteleme ve iptalde gerekçe zorunludur (`REASON_REQUIRED`).
 * - Kalkmış, geri çekilmiş ya da iptal edilmiş revizyon tekrar yayınlanamaz
 *   (`INVALID_TRANSITION`) [ISM 11.2.3]. Yalnız zamanlanmış revizyon ertelenir ve iptal edilir.
 */
export function assertTransition(
  state: RevisionState,
  event: RevisionEventKind,
  context: TransitionContext = {},
): RevisionState {
  const next = TRANSITIONS[state][event];
  if (next === undefined) {
    if (event === 'published' && (state === 'draft' || state === 'pending_approval')) {
      throw new DomainError('NOT_APPROVED', state);
    }
    if ((event === 'approved' || event === 'returned') && state === 'draft') {
      throw new DomainError('NOT_SUBMITTED', state);
    }
    throw new DomainError('INVALID_TRANSITION', `${state} → ${event}`);
  }
  if (REASON_REQUIRED_EVENTS.includes(event) && (context.reason ?? '').trim() === '') {
    throw new DomainError('REASON_REQUIRED', event);
  }
  return event === 'published' ? released(context.effectiveOn, context.asOf) : next;
}

/** Revizyonun yürürlük tarihi: son yayın ya da erteleme olayınınki; boşsa yayın anında. */
export function effectiveOn(events: readonly RevisionEvent[]): string | null {
  let value: string | null = null;
  for (const e of events) {
    if (e.event === 'published' || e.event === 'rescheduled') value = e.effectiveOn ?? null;
  }
  return value;
}

/** Türetmede kullanılan iç evre: yayınlanmış (zamanlanmış ya da yürürlükte) tek evredir. */
type Phase =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'released'
  | 'withdrawn'
  | 'discarded'
  | 'cancelled';

const FOLD: Readonly<Record<Phase, Partial<Readonly<Record<RevisionEventKind, Phase>>>>> = {
  draft: { submitted: 'pending_approval', discarded: 'discarded' },
  pending_approval: { returned: 'draft', approved: 'approved' },
  approved: { published: 'released', withdrawn: 'withdrawn' },
  // Erteleme ve iptalin zamanı yazılırken denetlenir; türetme olay zamanına bakmaz
  released: { rescheduled: 'released', cancelled: 'cancelled', withdrawn: 'withdrawn' },
  withdrawn: {},
  discarded: {},
  cancelled: {},
};

/**
 * Tek revizyonun durumu, olaylarından (eskiden yeniye) ve `asOf` gününe göre. "Yürürlükten
 * kalktı" dal düzeyinde türer (`branchStates`). Tutarsız geçmiş sessizce yorumlanmaz
 * (`INVALID_EVENT_HISTORY`). Onay olayıyla başlayan geçmiş gemideki kopyadır (ADR 0032).
 */
export function revisionState(events: readonly RevisionEvent[], asOf: string): RevisionState {
  // Gemideki kopyada geçmiş onaydan başlar: gönderim ve iade gemiye inmez (`REPLICA_EVENTS`).
  // Bulutta onay her zaman bir gönderimden sonra gelir; bu başlangıç orada hiç oluşmaz
  let phase: Phase = events[0]?.event === 'approved' ? 'pending_approval' : 'draft';
  for (const { event } of events) {
    const next: Phase | undefined = FOLD[phase][event];
    if (next === undefined) {
      throw new DomainError('INVALID_EVENT_HISTORY', `${phase} → ${String(event)}`);
    }
    phase = next;
  }
  return phase === 'released' ? released(effectiveOn(events), asOf) : phase;
}

/** Dalın bir revizyonu ve olayları (dal düzeyinde türetme için). */
export interface BranchRevisionEvents {
  id: string;
  seqNo: number;
  events: readonly RevisionEvent[];
}

/**
 * Dalın revizyonlarının durumları. Yürürlükteki revizyon, kendisinden sonraki bir revizyon
 * yayınlanıp yürürlüğe girince (`asOf ≥ effective_on`, boşsa hemen) "yürürlükten kalktı" olur.
 * Halef iptal edildiyse önceki kalır; halef geri çekildiyse önceki yine halefin yürürlük
 * tarihinde kalkar, örtük canlanma yoktur [sorular #26, #27; ISM 11.2.3].
 *
 * Zamanlanmış revizyonun sonraki bir halefi şimdiden yürürlükteyse o revizyon hiç yürürlüğe
 * girmeyecektir ve "yürürlükten kalktı" sayılır: bulut D gününde S'yi yürürlükte görüp T'nin hemen
 * yayınına izin verir; UTC'nin batısında hâlâ D−1 olan gemide S "yaklaşan" gösterilmez, ön teyidi
 * istenmez. Dalda en çok bir yürürlükte ve ondan önce bir zamanlanmış revizyon kalır (#27).
 */
export function branchStates(
  branch: readonly BranchRevisionEvents[],
  asOf: string,
): Map<string, RevisionState> {
  const ordered = [...branch].sort((a, b) => a.seqNo - b.seqNo);
  const retiring = ordered.filter((revision) => {
    const kinds = revision.events.map((e) => e.event);
    if (!kinds.includes('published') || kinds.includes('cancelled')) return false;
    const effective = effectiveOn(revision.events);
    return effective === null || effective <= asOf;
  });
  const result = new Map<string, RevisionState>();
  for (const revision of ordered) {
    const own = revisionState(revision.events, asOf);
    const retired =
      (own === 'published' || own === 'scheduled') &&
      retiring.some((successor) => successor.seqNo > revision.seqNo);
    result.set(revision.id, retired ? 'superseded' : own);
  }
  return result;
}

/** Dalın bir revizyonu: sıra no, türetilmiş durum, yayın olayı aldı mı ve revizyon no. */
export interface BranchRevision {
  id: string;
  seqNo: number;
  state: RevisionState;
  /** Hiç `published` olayı aldı mı (gemide görülmüş numara). */
  published: boolean;
  revisionNo: string;
}

/** Dalda açık revizyon varken yeni taslak açılamaz (`OPEN_REVISION_EXISTS`). */
export function assertCanOpenDraft(branch: readonly BranchRevision[]): void {
  const open = branch.find((revision) => OPEN_REVISION_STATES.includes(revision.state));
  if (open) throw new DomainError('OPEN_REVISION_EXISTS', open.id);
}

/** Dalda sıradaki numara: 1, 2, 3 … boşluksuz. */
export function nextSeqNo(branch: readonly BranchRevision[]): number {
  return branch.reduce((max, revision) => Math.max(max, revision.seqNo), 0) + 1;
}

/**
 * Yeni taslağın üst revizyonu: dalın yürürlükteki revizyonu; yoksa en son yürürlüğe girmiş olanı
 * (sonradan kalkmış ya da geri çekilmiş olsa da). İptal edilmiş zamanlanmış revizyon hiç
 * yürürlüğe girmediği için üst olmaz. Hiç yoksa `null` [sorular #26].
 */
export function parentForNewDraft(branch: readonly BranchRevision[]): string | null {
  const current = branch.find((revision) => revision.state === 'published');
  if (current) return current.id;
  const previous = branch
    .filter((revision) => revision.published && revision.state !== 'cancelled')
    .sort((a, b) => b.seqNo - a.seqNo);
  return previous[0]?.id ?? null;
}

const normalizedRevisionNo = (value: string) => value.trim().toLocaleLowerCase('en');

/**
 * Yeni revizyon no, dalda yayın olayı almış hiçbir revizyonunkiyle aynı olamaz
 * (`DUPLICATE_REVISION_NO`): bu numaralar gemide görülmüştür. Vazgeçilmiş ya da hiç yayınlanmamış
 * taslağın numarası yeniden kullanılabilir. Boşluk kırpılır, büyük/küçük harf ayrılmaz
 * [sorular #26].
 */
export function assertRevisionNoAvailable(
  branch: readonly BranchRevision[],
  revisionNo: string,
): void {
  const wanted = normalizedRevisionNo(revisionNo);
  const taken = branch.find(
    (revision) => revision.published && normalizedRevisionNo(revision.revisionNo) === wanted,
  );
  if (taken) throw new DomainError('DUPLICATE_REVISION_NO', revisionNo);
}

/**
 * Yeni kayıt (ör. form kaydı, M8b) yalnız yürürlükteki revizyonla açılır (`REVISION_NOT_CURRENT`)
 * [ISM 11.2.3]. Durum, kaydın oluşturulduğu günün (`recordAsOf`) durumudur.
 */
export function assertUsableForNewRecord(state: RevisionState): void {
  if (state !== 'published') throw new DomainError('REVISION_NOT_CURRENT', state);
}

/**
 * Yeni kaydın `asOf` günü: kaydın oluşturulduğu anın (gemi sunucusu saati) gemi yerel tarihi.
 * Kullanıcının girdiği iş tarihi kullanılmaz; geriye tarihli kayıt kalkmış revizyonla açılamaz
 * [sorular #27; ADR 0027 §13].
 */
export function recordAsOf(createdAtUtc: string, tzOffsetMin: number): string {
  return new Date(Date.parse(createdAtUtc) + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

/** Yayında yürürlük tarihi en çok bu kadar gün sonra olabilir (ADR 0028 #27). */
export const MAX_EFFECTIVE_DAYS_AHEAD = 365;
/** İptal ve ileri erteleme, yürürlükten bu kadar gün öncesine kadar yazılabilir. */
export const SCHEDULE_FREEZE_DAYS = 2;

function validDate(value: string): void {
  try {
    parseLocalDate(value);
  } catch {
    throw new DomainError('VALIDATION_FAILED', `effective_on ${value}`);
  }
}

/**
 * Yayında yürürlük tarihi (`effective_on`): boşsa hemen; en erken UTC yarın
 * (`EFFECTIVE_DATE_IN_PAST`), en geç 365 gün sonra (`EFFECTIVE_DATE_TOO_FAR`) [sorular #27].
 */
export function assertEffectiveOn(effective: string | null, todayUtc: string): void {
  if (effective === null) return;
  validDate(effective);
  if (effective < addDays(todayUtc, 1)) throw new DomainError('EFFECTIVE_DATE_IN_PAST', effective);
  if (effective > addDays(todayUtc, MAX_EFFECTIVE_DAYS_AHEAD)) {
    throw new DomainError('EFFECTIVE_DATE_TOO_FAR', effective);
  }
}

/**
 * Zamanlanmış revizyonun iptali yalnız UTC bugünü ≤ D − 2 iken (`SCHEDULE_FROZEN`): dünyanın hiçbir
 * saat diliminde D başlamamışken [sorular #27].
 */
export function assertCancel(effective: string, todayUtc: string): void {
  if (todayUtc > addDays(effective, -SCHEDULE_FREEZE_DAYS)) {
    throw new DomainError('SCHEDULE_FROZEN', effective);
  }
}

/**
 * Erteleme: tarihi ileri almak iptal gibi D − 2'den sonra donar (`SCHEDULE_FROZEN`); öne çekmek
 * D'ye kadar serbesttir, yeni tarih en erken UTC yarın. Her iki yönde en geç 365 gün sonra
 * [sorular #27].
 */
export function assertReschedule(current: string, next: string, todayUtc: string): void {
  validDate(next);
  if (next === current) throw new DomainError('VALIDATION_FAILED', 'same effective_on');
  if (next > current) assertCancel(current, todayUtc);
  assertEffectiveOn(next, todayUtc);
}

export interface SelfApprovalCheck {
  approverId: string;
  /** Revizyonu yükleyen (`created_by`). */
  uploaderId: string;
  /** Revizyonu onaya gönderenlerin hepsi: iadeden sonraki yeniden gönderimler dahil. */
  submitterIds: readonly string[];
  /** Taslağa ek ya da baskı PDF'i ekleyenler: onaylanan içeriğe katkıda bulunanlar (#34). */
  contributorIds?: readonly string[];
  /** Onay matrisindeki gerekçeli istisna (mimari belge §7.1); yoksa kapalı. */
  selfApprovalReason?: string | null;
}

/**
 * Dört göz (mimari belge §7.1): revizyonu yükleyen, içeriğine ek ya da baskı PDF'i ekleyen ve onu
 * herhangi bir gönderimde onaya gönderen kişi onaylayamaz (`SELF_APPROVAL`) [sorular #22, #34].
 * Onaylanan içerik hash'i ekleri de kapsadığından ek ekleyen de içeriğin yazarıdır. İstisna yalnız
 * matriste gerekçesi yazılıysa açılır.
 */
export function assertNotSelfApproval(check: SelfApprovalCheck): void {
  const own = new Set([check.uploaderId, ...check.submitterIds, ...(check.contributorIds ?? [])]);
  if (own.has(check.approverId) && (check.selfApprovalReason ?? '').trim() === '') {
    throw new DomainError('SELF_APPROVAL');
  }
}
